import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import { sortArrayByKey } from "@/lib/utils/sorting";
import dayjs from "dayjs";
import { parseDate } from "@internationalized/date";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { Button, ButtonGroup } from "@heroui/react";
import DateInput from "@/components/inputs/DateInput";
import TextInput from "@/components/inputs/TextInput";
import NumberInputComponent from "@/components/inputs/NumberInput";
import SelectButtonInput from "@/components/inputs/SelectButtonInput";
import { useFormContext } from "react-hook-form";
import { CropConfiguration } from "@/types/cropConfiguration";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

function roundHalfEven(value: number) {
    const lower = Math.floor(value);
    return value - lower === 0.5
        ? lower % 2 === 0
            ? lower
            : lower + 1
        : Math.round(value);
}

function PlantingCropDetailsForm({
    crops,
    selectedCrop,
}: {
    crops: { id: number; description: string }[];
    selectedCrop?: CropConfiguration;
}) {
    const { t } = useTranslation(["operations", "messages"]);
    const {
        setValue,
        formState: { errors },
    } = useFormContext();
    const [showGDUs, setShowGDUs] = useState(false);
    const supportsCrm =
        selectedCrop?.planting_form_fields.includes("crm") ?? false;

    useEffect(() => {
        if (
            selectedCrop &&
            !selectedCrop.planting_form_fields.includes("crm")
        ) {
            setValue("crm", null);
            setValue("gdd_silk", null);
            setValue("gdd_mature", null);
        }
    }, [selectedCrop, setValue]);

    useEffect(() => {
        if (!supportsCrm) return;
        if (errors.crm) setShowGDUs(false);
        else if (errors.gdd_silk || errors.gdd_mature) setShowGDUs(true);
    }, [supportsCrm, errors.crm, errors.gdd_silk, errors.gdd_mature]);

    return (
        <>
            <AutoCompleteInput
                name="crop_configuration"
                isRequired
                options={sortArrayByKey(crops, "description")}
                labelPlacement="outside"
                placeholder="Select Crop"
                label={t("crop")}
                size="sm"
            />
            <div className="flex flex-row gap-2 items-end ">
                <DateInput
                    name="start_date"
                    label={t("plantingDate")}
                    variant="flat"
                    isRequired
                    size="sm"
                    hideErrorMessage
                    labelPlacement="outside"
                    showMonthAndYearPickers
                    placeholderValue={parseDate(dayjs().format("YYYY-MM-DD"))}
                />
                <div>
                    <Button
                        size="sm"
                        onPress={() => {
                            setValue(
                                "start_date",
                                dayjs(
                                    selectedCrop?.default_planting_date,
                                ).toDate(),
                            );
                        }}
                        color="primary"
                        isDisabled={!selectedCrop}
                        startContent={<FontAwesomeIcon icon={faCalendar} />}
                    >
                        {t("default")}
                    </Button>
                </div>
            </div>
            <div className="flex flex-row gap-2 items-end self-start w-full">
                <TextInput
                    name="hybrid"
                    label={t("hybrid")}
                    labelPlacement="outside"
                    size="sm"
                    placeholder={t("hybrid")}
                    className={
                        supportsCrm
                            ? "w-1/3 min-w-0 shrink-0"
                            : "min-w-0 flex-1"
                    }
                />
                {selectedCrop?.planting_form_fields.includes(
                    "maturity_level",
                ) && (
                    <SelectButtonInput
                        name="maturity_level"
                        label={t("maturityLevel")}
                        size="sm"
                        orientation="horizontal"
                        classNames={{
                            base: "flex-1 w-full",
                            label: "text-xs",
                        }}
                        radioProps={{
                            size: "sm",
                        }}
                        options={[
                            { description: t("early"), id: "early" },
                            { description: t("medium"), id: "medium" },
                            { description: t("late"), id: "late" },
                        ]}
                    />
                )}
                {selectedCrop?.planting_form_fields.includes("rice_type") && (
                    <SelectButtonInput
                        name="rice_type"
                        label="Rice Type"
                        size="sm"
                        orientation="horizontal"
                        classNames={{
                            base: "flex-1 w-full",
                            label: "text-xs",
                        }}
                        radioProps={{
                            size: "sm",
                        }}
                        options={[
                            { description: "American", id: "american" },
                            { description: "Japanese", id: "japanese" },
                        ]}
                    />
                )}
                {selectedCrop?.planting_form_fields.includes(
                    "sugarcane_type",
                ) && (
                    <SelectButtonInput
                        name="sugarcane_type"
                        label="Sugarcane Type"
                        size="sm"
                        orientation="horizontal"
                        classNames={{
                            base: "flex-1 w-full",
                            label: "text-xs",
                        }}
                        radioProps={{
                            size: "sm",
                        }}
                        options={[
                            {
                                description: "High Fiber",
                                id: "high_fiber",
                            },
                            {
                                description: "High Sucrose",
                                id: "high_sucrose",
                            },
                        ]}
                    />
                )}
                {selectedCrop?.planting_form_fields.includes(
                    "soybean_maturity_group",
                ) && (
                    <TextInput
                        type="number"
                        min={1}
                        max={11}
                        decimalPlaces={1}
                        name="soybean_maturity_group"
                        label={t("maturityLevel")}
                        placeholder="1"
                        labelPlacement="outside"
                        size="sm"
                        className="min-w-0 flex-1"
                    />
                )}
                {supportsCrm && (
                    <>
                        <div className="min-w-0 flex-1">
                            {showGDUs ? (
                                <div className="grid w-full grid-cols-2 gap-2">
                                    <NumberInputComponent
                                        name="gdd_silk"
                                        label="GDU to silk"
                                        labelPlacement="outside"
                                        size="sm"
                                        placeholder=" "
                                    />
                                    <NumberInputComponent
                                        name="gdd_mature"
                                        label="GDU to maturity"
                                        labelPlacement="outside"
                                        size="sm"
                                        placeholder=" "
                                    />
                                </div>
                            ) : (
                                <TextInput
                                    type="number"
                                    name="crm"
                                    label="CRM"
                                    placeholder="100"
                                    labelPlacement="outside"
                                    size="sm"
                                    className="w-full"
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        const crm = Number(value);
                                        if (
                                            value !== "" &&
                                            Number.isInteger(crm)
                                        ) {
                                            setValue(
                                                "gdd_silk",
                                                roundHalfEven(
                                                    10.133 * crm + 232.7,
                                                ),
                                            );
                                            setValue(
                                                "gdd_mature",
                                                roundHalfEven(
                                                    24.268 * crm + 30.22,
                                                ),
                                            );
                                        }
                                    }}
                                />
                            )}
                        </div>
                        <ButtonGroup size="sm" variant="flat" className="shrink-0">
                            <Button
                                size="sm"
                                color={showGDUs ? "default" : "primary"}
                                onPress={() => setShowGDUs(false)}
                            >
                                CRM
                            </Button>
                            <Button
                                size="sm"
                                color={showGDUs ? "primary" : "default"}
                                onPress={() => setShowGDUs(true)}
                            >
                                GDUs
                            </Button>
                        </ButtonGroup>
                    </>
                )}
            </div>
        </>
    );
}

export default PlantingCropDetailsForm;
